import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, Award, Waves, Trophy, Sparkles } from 'lucide-react';
import useReveal from '../hooks/useReveal';
import { meetKate, site } from '../mock/mock';

const creds = [
  { icon: Award, title: 'NAVP Registered', text: 'Fully qualified and insured, and registered with the National Association of Veterinary Physiotherapists.' },
  { icon: Waves, title: 'Equine Swimming Centre', text: 'Years of hands-on rehabilitation experience, monitoring individual cases from admission to discharge.' },
  { icon: Trophy, title: 'Lead Racing Yard Physio', text: 'Lead equine physiotherapist on a racing yard, optimising performance alongside vets, trainers and farriers.' },
  { icon: Sparkles, title: 'Bespoke Treatment', text: 'Every plan is tailored to the individual animal, with achievable home exercises between sessions.' },
];

export default function About() {
  useReveal();
  return (
    <div className="pt-24">
      {/* Header */}
      <section className="bg-cream pt-16 pb-20 text-center">
        <div className="max-w-3xl mx-auto px-5">
          <p className="eyebrow text-sage-500 mb-3">About</p>
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-ink mb-4">Meet Kate Darlison</h1>
          <p className="text-ink/60 text-lg">The physiotherapist behind KD Veterinary Physiotherapy.</p>
        </div>
      </section>

      {/* Bio */}
      <section className="py-24">
        <div className="max-w-[1120px] mx-auto px-5 grid md:grid-cols-2 gap-14 items-center">
          <div className="reveal rounded-3xl overflow-hidden shadow-xl">
            <img src={meetKate.image} alt={meetKate.name} className="w-full h-full object-cover aspect-square" />
          </div>
          <div className="reveal">
            <p className="text-sage-600 font-semibold mb-1">{meetKate.role}</p>
            <p className="text-ink/50 text-sm mb-6">{meetKate.creds}</p>
            <div className="space-y-5 text-ink/70 leading-relaxed">
              <p>Kate is a fully qualified and insured, NAVP-registered veterinary physiotherapist serving the West Midlands, Warwickshire and surrounding areas. She works to improve mobility, maintain comfort and ensure the ongoing quality of life of equines, canines and other family pets.</p>
              <p>Kate’s experience has included working in the rehabilitation of horses at an equine swimming centre for a number of years. Here, Kate worked closely with individual cases, monitoring their conditions and progress throughout their stay, as well as completing equine rehabilitation and strength & conditioning programmes for preventative therapy and improving the performance of competition horses.</p>
              <p>Kate is also the lead equine physiotherapist on a racing yard, working closely alongside other multi-disciplinary teams such as vets, racing trainers, and farriers to help optimise the performance of horses and secure results.</p>
            </div>
            <Link to="/contact" className="mt-8 inline-flex items-center gap-2 bg-sage-500 hover:bg-sage-600 text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg">
              Book with Kate <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Credentials */}
      <section className="bg-cream py-24">
        <div className="max-w-[1120px] mx-auto px-5">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-ink text-center mb-14 reveal">Experience & Credentials</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-7">
            {creds.map((c, i) => (
              <div key={c.title} className="bg-white rounded-2xl p-8 shadow-sm border border-cream-dark reveal" style={{ transitionDelay: `${i * 70}ms` }}>
                <div className="w-14 h-14 rounded-xl bg-sage-50 flex items-center justify-center mb-5">
                  <c.icon size={26} className="text-sage-600" />
                </div>
                <h3 className="text-xl font-serif text-ink mb-3">{c.title}</h3>
                <p className="text-ink/65 text-sm leading-relaxed">{c.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-2xl mx-auto px-5 text-center reveal">
          <h2 className="text-4xl font-serif font-bold text-ink mb-4">Ready to help your animal feel their best?</h2>
          <p className="text-ink/65 mb-8">Call or email Kate to discuss treatment options. Veterinary consent is required prior to an initial consultation.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={site.phoneHref} className="inline-flex items-center justify-center gap-2 bg-sage-500 hover:bg-sage-600 text-white font-semibold px-8 py-3.5 rounded-full transition-all hover:-translate-y-0.5 hover:shadow-lg">
              <Phone size={18} /> Call {site.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 border-2 border-sage-500 text-sage-600 hover:bg-sage-500 hover:text-white font-semibold px-8 py-3.5 rounded-full transition-all">
              Send an Enquiry <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
